import React from 'react';
import Header from '@amzn/awsui-components-react/polaris/header';
import { Event, Source } from '@amzn/red-velvet-api';
import { useSearchParams } from 'react-router-dom';
import { useUser } from '../../hooks/user';
import { featureIsEnabled, useFeatures } from '../../hooks/features';
import { ADMIN_EVENT_PAGE_ACCESS } from '../../utils/constants';
import { canEditEvent, canManageVCEvent } from '../../utils/shiftUtils';
import { EditEventLink } from '../shifts/actions';
import { SpaLink } from '../SpaLink';

interface EventTitleHeaderProps {
  event: Event;
  /** Analytics source passed to EditEventLink for manageable GIVEHUB events. */
  editSource?: string;
  /** data-aci-analytics-name for the (GIVEHUB) title link. */
  titleAnalyticsName?: string;
  /**
   * Renders the edit control for VC events, disabled when the user can't edit the event. VC edit
   * flows differ per page (e.g. attendance re-enters the wizard), so the caller supplies the control.
   */
  renderVcEditAction?: (disabled: boolean) => React.ReactNode;
}

/**
 * Shared event title header: links the title to the event details page for GIVEHUB events,
 * shows a plain title for VC events, and renders the appropriate edit affordance for coordinators
 * (disabled, with a reason, for users who can't edit the event).
 */
export function EventTitleHeader({
  event,
  editSource,
  titleAnalyticsName,
  renderVcEditAction,
}: EventTitleHeaderProps) {
  const user = useUser();
  const [searchParams] = useSearchParams();
  const selectedTimezone = searchParams.get('timezone');
  const urlFeatures = searchParams.get('features');
  const features = useFeatures();
  const isAdmin = featureIsEnabled(ADMIN_EVENT_PAGE_ACCESS, features, searchParams);

  const isGivehub = event.source === Source.GIVEHUB;

  // Users who can open this page but can't edit the event (e.g. direct-link visitors) get a
  // disabled Edit button with a reason. Admins can edit GIVEHUB events. VC pages without
  // renderVcEditAction show no edit control.
  let headerAction: React.ReactNode;
  if (isGivehub) {
    headerAction = (
      <EditEventLink
        event={event}
        source={editSource}
        disabled={!canEditEvent(event, user, isAdmin)}
      />
    );
  } else if (renderVcEditAction) {
    headerAction = renderVcEditAction(!canManageVCEvent(event, user));
  }

  const viewDetailsUrl = `/view-event?id=${event.eventId}${selectedTimezone ? '&timezone=' + selectedTimezone : ''}${urlFeatures ? '&features=' + urlFeatures : ''}`;

  return (
    <Header variant="h2" actions={headerAction}>
      {isGivehub ? (
        <SpaLink data-aci-analytics-name={titleAnalyticsName} href={viewDetailsUrl}>
          <span>{event.title}</span>
        </SpaLink>
      ) : (
        <span>{event.title}</span>
      )}
    </Header>
  );
}
